<template>
  <article class="import-lineups-wrapper">
    <section class="import-lineups-container">
      <header-simple :title="title" />
      <stepper :steps="steps" :current="currentStep" />
      <hr class="divider" height="1px" width="90%" />
      <div class="import-lineups-step__container">
        <div class="import-lineups-step step-0" v-if="currentStep == 0">
          <div class="import-lineups-result">
            <div class="import-lineups-result__header">
              <h3 class="import-lineups-result__title icon-file-excel">
                Step 1: Prepare your file
              </h3>
            </div>
            <div class="import-lineups-result__content">
              <p>
                To import your lineup, format your Excel file (xlsx) using the following names for
                your columns and formats for your cells:
              </p>
              <import-format-table :headers="columnsFormat.headers" :body="columnsFormat.body" />
            </div>
            <div class="import-lineups-result__header">
              <h3 class="import-lineups-result__title icon-file-add">
                Step 2: Upload your formatted file
              </h3>
            </div>
            <div class="import-lineups-result__content">
              <div class="import-lineups__file-container">
                <form action="" enctype="multipart/form-data" class="mds-form">
                  <div class="mds-form__field-group">
                    <input
                      class="mds-form__input import-lineups__file-input"
                      type="file"
                      accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      @change="onSelectFile"
                      ref="file"
                    />
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div class="actions-bar">
            <div class="box left">
              <button class="mds-button mds-button--secondary" type="button" @click="cancelImport">
                Cancel
              </button>
            </div>
            <div class="box right">
              <mds-button class= "lineups-button__start-file-validation"
              variation="primary"
              :disabled="!canContinue"
              @click="startValidation"
              >
                Continue
              </mds-button>
            </div>
          </div>
        </div>
        <div class="import-lineups-step step-1" v-if="currentStep == 1">
          <template v-if="!this.error">
            <loading-spinner />
            <h3 class="import-lineups-step__title centered">Checking {{ fileName }}</h3>
          </template>
          <template v-else>
            <h3 v-if="hasValidationFailed" class="import-lineups-step__title">
              We found a few problems and were unable to import your lineups.<br />
              Please review issues below and update your spreadsheet.
            </h3>
            <h3 v-else class="import-lineups-step__title">
              Our check is complete. Please review the items below.
            </h3>

            <div class="import-lineups-result__container">
              <div class="import-lineups-result" v-if="errorMessage">
                <div class="import-lineups-result__header">
                  <h3 class="import-lineups-result__title import-lineups-result__title icon-not-ready">
                    There has occurred an error in the importing lineups process
                  </h3>
                </div>
                <div class="import-lineups-result__content import-lineups-result__error">
                  <p>
                    {{errorMessage}}
                  </p>
                </div>
              </div>

              <div class="import-lineups-result" v-if="isInvalidExtensionCheck">
                <div class="import-lineups-result__header">
                  <h3 class="import-lineups-result__title icon-not-ready">
                    Your spreadsheet has an extension issue. Only *.xlsx files allowed.
                  </h3>
                </div>
                <div class="import-lineups-result__content">
                  <p>
                    Check the format of your Excel file with the proper extension (xlsx) to make sure
                    you are importing a valid file:
                  </p>
                  <import-format-table :headers="columnsFormat.headers" :body="columnsFormat.body" />
                </div>
              </div>

              <div class="actions-bar">
                <div class="box left">
                  <button class="mds-button mds-button--flat" @click="startAgain">
                    <svg class="mds-icon mds-button__icon mds-button__icon--left" aria-hidden="true">
                      <use xlink:href="static/mds/icons/mds.svg#caret-left--s"></use>
                    </svg>
                    <span class="mds-button__text">
                      Back
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </template>
        </div>

        <div class="import-lineups-step step-2" v-if="currentStep == 2">
          <h3 v-if="hasValidationFailed" class="import-lineups-step__title">
            We found a few problems and were unable to import your lineups.<br />
            Please review issues below and update your spreadsheet.
          </h3>
          <h3 v-else class="import-lineups-step__title">
            Our check is complete. Please review the items below.
          </h3>

          <div class="import-lineups-result__container">
            <div
              class="import-lineups-result"
              v-if="hasValidationFailed && checkInvalidStatus(['InvalidFormat'])"
            >
              <div class="import-lineups-result__header">
                <h3 class="import-lineups-result__title icon-not-ready">
                  Your spreadsheet has formatting issues
                </h3>
              </div>
              <div class="import-lineups-result__content import-lineups-result__error">
                <p>
                  Check the format of your Excel file (xlsx) to make sure you are using the following
                  names for your columns and formats for your cells:
                </p>
                <import-format-table :headers="columnsFormat.headers" :body="columnsFormat.body" />
              </div>
            </div>

            <div
              class="import-lineups-result"
              v-if="hasValidationFailed && checkInvalidStatus(['UnknownProductCode'])"
            >
              <div class="import-lineups-result__header">
                <h3 class="import-lineups-result__title icon-not-ready">
                  We don't recognize these products:
                </h3>
              </div>
              <div class="import-lineups-result__content">
                <mds-button
                  class="import-lineup-error-download"
                  variation="primary"
                  @click="downloadErrorForProduct"
                >
                  Export Issues
                </mds-button>
                <p>Review the following products:</p>
                <p class="table-heading"><strong>Product Code</strong></p>
                <table class="mds-data-table alternate-color-table unknown-table">
                  <caption class="mds-data-table__caption">
                    List of funds with unknown product codes
                  </caption>
                  <tbody>
                    <tr
                      class="mds-data-table__row"
                      v-for="(item, index) in trimInvalidLineups('UnknownProductCode')"
                      :key="index"
                    >
                      <td class="mds-data-table__cell" :title="getDetailedErrorMessage(item)">
                        {{ formatProductName(item.rowIdentifier.product) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div
              class="import-lineups-result"
              v-if="
                hasValidationFailed && !checkInvalidStatus(['InvalidFormat', 'UnknownProductCode'])
              "
            >
              <div class="import-lineups-result__header">
                <h3 class="import-lineups-result__title import-lineups-result__title icon-not-ready">
                  Lineups not ready for import
                </h3>
              </div>
              <div class="import-lineups-result__content import-lineups-result__error">
                <mds-button
                  class="import-lineup-error-download"
                  variation="primary"
                  @click="downloadErrors"
                >
                  Export Issues
                </mds-button>
                <p>
                  We can't import {{ validationOutput.failedImportedLineupsCount }} lineups. These
                  lineups will not be included in your import
                </p>
                <p class="view-funds-btn">
                  <button
                    class="mds-button mds-button--flat"
                    type="button"
                    @click="toggleDetails('invalid')"
                  >
                    <svg class="mds-icon mds-button__icon mds-button__icon--left" aria-hidden="true">
                      <use :xlink:href="setTriangle(openDetails.invalid)">
                        <title>open/close indicator</title>
                      </use>
                    </svg>
                    <span class="mds-button__text"> View Lineups </span>
                  </button>
                </p>
                <transition name="scale">
                  <lineups-table
                    v-show="openDetails.invalid"
                    type="invalid"
                    :lineups="validationOutput.invalid"
                  />
                </transition>
              </div>
            </div>

            <!-- ERROR -->
            <div
              class="import-lineups-result"
              v-if="validationOutput.invalid && validationStatus !== 'ValidationFailed'"
            >
              <div class="import-lineups-result__header">
                <h3 class="import-lineups-result__title import-lineups-result__title icon-not-ready">
                  Lineups not ready for import
                </h3>
              </div>
              <div class="import-lineups-result__content import-lineups-result__error">
                <mds-button
                  class="import-lineup-error-download"
                  variation="primary"
                  @click="downloadErrors"
                >
                  Export Issues
                </mds-button>
                <p>
                  We can't import {{ validationOutput.failedImportedLineupsCount }} lineups. These
                  lineups will not be included in your import
                </p>
                <p class="view-funds-btn">
                  <button
                    class="mds-button mds-button--flat"
                    type="button"
                    @click="toggleDetails('invalid')"
                  >
                    <svg class="mds-icon mds-button__icon mds-button__icon--left" aria-hidden="true">
                      <use :xlink:href="setTriangle(openDetails.invalid)">
                        <title>open/close indicator</title>
                      </use>
                    </svg>
                    <span class="mds-button__text"> View Lineups </span>
                  </button>
                </p>
                <transition name="scale">
                  <lineups-table
                    v-show="openDetails.invalid"
                    type="invalid"
                    :lineups="validationOutput.invalid"
                  />
                </transition>
              </div>
            </div>

            <!-- WARNINGS -->
            <div class="import-lineups-result" v-if="validationOutput.warnings">
              <div class="import-lineups-result__header">
                <h3
                  class="import-lineups-result__title import-lineups-result__title icon-fund-closed"
                >
                  Funds closed to new sales
                </h3>
              </div>
              <div class="import-lineups-result__content import-lineups-result__warning">
                <mds-button
                  class="import-lineup-error-download"
                  variation="primary"
                  @click="downloadWarnings"
                >
                  Export Issues
                </mds-button>
                <p>You are able to import these funds.</p>
                <p class="view-funds-btn">
                  <button
                    class="mds-button mds-button--flat lineups-button__toggle-details-warning"
                    type="button"
                    @click="toggleDetails('warning')"
                  >
                    <svg class="mds-icon mds-button__icon mds-button__icon--left" aria-hidden="true">
                      <use :xlink:href="setTriangle(openDetails.warning)">
                        <title>open/close indicator</title>
                      </use>
                    </svg>
                    <span class="mds-button__text"> View Funds </span>
                  </button>
                </p>
                <transition name="scale">
                  <div class="warnings-table-container">
                    <table
                      class="mds-data-table alternate-color-table warning-table"
                      v-show="openDetails.warning"
                    >
                      <caption class="mds-data-table__caption">
                        List of funds closed to new sales
                      </caption>
                      <thead class="mds-data-table__header">
                        <tr class="mds-data-table__row">
                          <th class="mds-data-table__header-cell" width="50%">Fund</th>
                          <th class="mds-data-table__header-cell" width="50%">Ticker</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          class="mds-data-table__row"
                          v-for="(item, index) in validationOutput.warnings"
                          :title="getDetailedErrorMessage(item)"
                          :key="index"
                        >
                          <td class="mds-data-table__cell warning-body-cell" width="50%">
                            {{ item.rowIdentifier.fundName }}
                          </td>
                          <td class="mds-data-table__cell warning-body-cell" width="50%">
                            {{ item.rowIdentifier.ticker }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
            </div>

            <!-- SUCCESS -->
            <div class="import-lineups-result" v-if="validationOutput.valid">
              <div class="import-lineups-result__header">
                <h3 class="import-lineups-result__title import-lineups-result__title icon-ready">
                  Lineups ready for import
                </h3>
              </div>
              <div class="import-lineups-result__content import-lineups-result__success">
                <p :title="`${successfullyImportedLineupFundsCount} funds`">
                  Click continue to import {{ successfullyImportedLineupsCount }} lineups
                </p>
              </div>
            </div>

            <!-- ACTION BAR -->
            <div class="actions-bar">
              <div class="box left">
                <button class="mds-button mds-button--flat" @click="startAgain">
                  <svg class="mds-icon mds-button__icon mds-button__icon--left" aria-hidden="true">
                    <use xlink:href="static/mds/icons/mds.svg#caret-left--s"></use>
                  </svg>
                  <span class="mds-button__text">
                    Back
                  </span>
                </button>
              </div>
              <div class="box right">
                <button
                  v-if="validationStatus !== 'ValidationFailed'"
                  class="mds-button mds-button--secondary lineups-button__cancel-import"
                  type="button"
                  @click="cancelImport"
                >
                  Cancel
                </button>
                <mds-button v-if="validationStatus === 'ValidationFailed'"
                  variation="primary"
                  @click="cancelImport"
                >
                  Return to All Clients
                </mds-button>
                <mds-button v-else
                  class="lineups-button__start-import"
                  variation="primary"
                  @click="startImport"
                >
                  Continue
                </mds-button>
              </div>
            </div>
          </div>
        </div>

        <div class="import-lineups-step" v-if="currentStep == 3">
          <h3 class="import-lineups-step__title centered">Importing {{ fileName }}</h3>
          <progress-bar :duration="totalValidateTime" :enabled="currentStep == 3" />
        </div>
      </div>
    </section>
    <notification :show-contact-info-enabled="true" />
    <modal />
  </article>
</template>
<script>
import { mapActions } from 'vuex';
import {
  concat,
  filter,
  get,
  includes,
  map,
  reduce,
} from 'lodash';

import { columnsFormat, steps } from '@/config/import-lineups-format.json';
import { goToLogoutRoute, goToRoute } from '@/utils';
import HeaderSimple from '@/components/atoms/HeaderSimple';
import LoadingSpinner from '@/components/atoms/LoadingSpinner';
import Modal from '@/components/atoms/Modal';
import Notification from '@/components/atoms/Notification';
import Stepper from '@/components/atoms/Stepper';
import ProgressBar from '@/components/atoms/ProgressBar';
import LineupsTable from '@/components/atoms/LineupsTable';
import ImportFormatTable from '@/components/atoms/ImportFormatTable';
import ErrorNotificationMixin from '@/mixins/ErrorNotificationMixin';
import { exportExcel } from '@/utils/misc';

/* eslint import/no-cycle: [2, { maxDepth: 1 }] */
import * as LineupsFundsService from '@/services/LineupsFunds';

export default {
  name: 'import-lineups',
  components: {
    HeaderSimple,
    Stepper,
    LoadingSpinner,
    LineupsTable,
    ProgressBar,
    Notification,
    Modal,
    ImportFormatTable,
  },
  mixins: [
    ErrorNotificationMixin({ component: 'ImportLineups' }),
  ],
  data() {
    return {
      title: 'Import Lineups',
      columnsFormat,
      steps,
      currentStep: 0,
      canContinue: false,
      error: false,
      filePath: '',
      startValidateTime: 0,
      totalValidateTime: 0,
      validationOutput: {},
      validationStatus: '',
      invalidStatuses: [],
      openDetails: {
        invalid: false,
        warning: false,
      },
      fileName: '',
      errorMessage: '',
      importCompleted: false,
      request: null,
      requestController: null,
      requestUrl: null,
      toTheActualAPI: true,
    };
  },
  computed: {
    isInvalidExtensionCheck() {
      return (
        this.validationStatus === 'ValidationFailed'
        && this.checkInvalidStatus(['InvalidExtension'])
      );
    },
    successfullyImportedLineupFundsCount() {
      return Array.isArray(this.validationOutput.valid) && this.validationOutput.valid.length;
    },
    successfullyImportedLineupsCount() {
      return get(this.validationOutput, 'successfullyImportedLineupsCount', 0);
    },
    hasValidationFailed() {
      return this.validationStatus === 'ValidationFailed';
    },
  },
  methods: {
    ...mapActions(['logError']),
    cancelImport() {
      goToRoute(this.$route, this.$router, '/');
    },
    onSelectFile(event) {
      this.canContinue = true;
      this.filePath = '';
      if (!this.validateFile(event.target)) {
        this.canContinue = false;
        this.error = true;
        this.filePath = event.target.value;
        this.currentStep = 1;
        this.validationStatus = 'ValidationFailed';
        this.invalidStatuses.push('InvalidExtension');
      }
    },
    validateFile(fileObj) {
      const isNotEmpty = fileObj.value !== '';
      const isOnlyOneFile = fileObj.files && fileObj.files.length === 1;
      const extension = fileObj.value.split('.').pop();
      const hasAllowedExtensionFile = ['xlsx'].indexOf(extension) >= 0;
      return isNotEmpty && isOnlyOneFile && hasAllowedExtensionFile;
    },
    toggleDetails(section) {
      this.openDetails[section] = !this.openDetails[section];
    },
    async startValidation() {
      const file = get(this.$refs, 'file.files[0]', {});
      this.fileName = file.name;
      this.currentStep = 1;
      this.startValidateTime = new Date().getTime();

      this.error = false;
      this.errorMessage = '';

      LineupsFundsService.validateLineupsFile({
        file,
      }).then((response) => {
        this.error = false;
        this.errorMessage = '';
        this.validationOutput = get(response, 'data', {});
        this.validationStatus = get(this.validationOutput, 'status', 'ValidationFailed');

        if (!response.status.success) {
          // General Error and the error message will be inside the response.status object
          this.error = true;
          this.errorMessage = response.status.message;
          this.invalidStatuses = ['InvalidData'];
          this.validationOutput = {
            ...this.validationOutput,
            invalid: map(this.validationOutput.invalid, (invalidData, index) => ({
              ...invalidData,
              secId: invalidData.secId || `invalid-${index}`,
            })),
          };
        } else {
          // There could be one or more errors but more related to a specific row of the file
          this.invalidStatuses = [
            ...new Set(
              reduce(
                this.validationOutput.invalid,
                (list, item) => concat(list, item.errors),
                [],
              ),
            ),
          ];
        }
        this.currentStep = 2;
        this.totalValidateTime = Math.ceil(
          (new Date().getTime() - this.startValidateTime) / 1000,
        );
      }).catch((error) => {
        this.error = true;
        this.errorMessage = error.message;
        this.validationStatus = 'ValidationFailed';
        this.invalidStatuses = ['InvalidData'];
        this.notifyError({
          error,
          section: 'methods:startValidation',
          data: { url: this.requestUrl },
          notificaction: {
            title: 'Validation Lineups Error',
            message: 'Validation of Lineups process has pesented an error.',
            showContactInfo: true,
          },
        });
      });
    },
    checkInvalidStatus(value) {
      let invalid = false;
      map(value, (v) => {
        if (includes(this.invalidStatuses, v)) {
          invalid = true;
        }
      });
      return invalid;
    },
    trimInvalidLineups(value) {
      return filter(this.validationOutput.invalid, (o) => includes(o.errors, value));
    },
    formatProductName(product) {
      if (product === '') {
        return 'There is missing data in the Product column';
      }
      return product;
    },
    downloadErrors() {
      const jsonData = this.getErrorDataForExcel(this.validationOutput.invalid);
      exportExcel(jsonData, 'Import_Lineup_Issues');
    },
    getErrorDataForExcel(errorData) {
      const errorJson = errorData.map((item) => {
        const { rowIdentifier, messages } = item;
        return {
          Fund: rowIdentifier.fundName,
          Program: rowIdentifier.program,
          Menu: rowIdentifier.menu,
          Ticker: rowIdentifier.ticker,
          SecId: rowIdentifier.secId,
          Description: messages[0],
        };
      });
      return errorJson;
    },
    downloadErrorForProduct() {
      const productErrors = this.trimInvalidLineups('UnknownProductCode');
      const errorJson = productErrors.map((item) => {
        return {
          Product: this.formatProductName(item.rowIdentifier.product),
        };
      });
      exportExcel(errorJson, 'Import_Lineup_Issues');
    },
    downloadWarnings() {
      const jsonData = this.getErrorDataForExcel(this.validationOutput.warnings);
      exportExcel(jsonData, 'Import_Lineup_Issues');
    },
    startAgain() {
      this.filePath = '';
      this.currentStep = 0;
      this.invalidStatuses = [];
      this.error = false;
      this.importCompleted = false;
      this.validationStatus = '';
      this.validationOutput = {};
      this.openDetails = {
        invalid: false,
        warning: false,
      };
    },
    startImport() {
      this.currentStep = 3;
      this.processing = true;
      this.startValidateTime = new Date().getTime();

      const payload = filter(this.validationOutput.valid, (item) => item.fiduciaryFundId);

      LineupsFundsService.importLineups({
        payload,
      }).then((result) => {
        this.processing = false;
        this.error = false;
        this.totalValidateTime = Math.ceil(
          (new Date().getTime() - this.startValidateTime) / 1000,
        );
        this.importCompleted = true;
        this.$router.push('/', () => {
          this.$store.dispatch('openNotification', {
            message: 'Import of lineups complete.',
            action: 'pending',
            actionLabel: 'View updates in Pending',
          });
        });
        return result;
      }).catch((error) => {
        this.error = true;
        this.currentStep = 2;
        this.loading = false;
        if (error.status === 401) {
          goToLogoutRoute(this.$route, this.$router);
        } else if (error.name !== 'AbortError') {
          this.notifyError({
            error,
            section: 'methods:startImport',
            data: { url: this.requestUrl },
            notificaction: {
              title: 'Importing Error',
              message: 'Import of Lineups process has pesented an error.',
              showContactInfo: true,
            },
          });
        }
      });
    },
    setTriangle(value) {
      if (value === false) {
        return 'static/mds/icons/mds.svg#triangle-fill-right';
      }
      return 'static/mds/icons/mds.svg#triangle-fill-down';
    },
    getDetailedErrorMessage(item) {
      return get(item, 'messages[0]', '');
    },
  },
  beforeDestroy() {
    this.requestController && this.requestController.abort();
  },
};
</script>

<style lang="scss">
.import-lineups-container {
  display: flex;
  flex-direction: column;
}
.import-lineups-wrapper {
  width: calc(100% - 85px);
}
.import-lineups-step {
  &__container {
    height: 100%;
    overflow-x: none;
    overflow-y: auto;
    padding: $mds-space-5-x $mds-space-8-x $mds-space-2-x $mds-space-8-x;
  }
  &__title {
    @include mds-level-4-heading();
  }
  .box {
    display: flex;
    justify-content: space-between;
    margin-top: $mds-space-8-x;
  }
  .centered {
    text-align: center;
  }
  p {
    @include mds-body-text-m();
  }
}
.import-lineups {
  &__check-list {
    font-family: $mds-font-family-sans-serif;
    font-size: $mds-font-size-m;
    font-weight: $mds-font-weight-thin;
    list-style: none;
    padding: 0;

    .check-list__item {
      background: url("/static/assets/check_icon_ct.svg") no-repeat left center;
      margin-bottom: $mds-space-inset-4-x;
      min-height: $mds-space-4-x;
      padding-right: $mds-space-inset-4-x;
      padding-left: 48px; // Match to deisgn
    }
  }
  &__file-container {
    background-color: $mds-background-color-light-gray;
    margin: $mds-space-4-x 0;
    padding: $mds-space-8-x;
  }
  &__file-input.mds-form__input {
    background-color: $mds-background-color-light-gray;
    box-shadow: none;
    height: 42px; // Match to design
    margin-top: $mds-space-2-x;
  }
  &__file-input.mds-form__input::-webkit-file-upload-button {
    border: 0;
    border-radius: $mds-space-inset-3-x;
    box-shadow: inset 0 0 0 1px $mds-interactive-color-primary-default;
    background-color: $mds-interactive-color-primary-default;
    color: $mds-color-white;
    padding: $mds-space-1-x $mds-space-2-x;
  }
}
.import-lineups-result {
  &__header {
    display: inline-block;
  }
  &__title {
    display: inline-block;
    @include mds-level-4-heading();
    font-weight: $mds-font-weight-bold;
    padding: $mds-space-inset-1-x 48px; // Match to design
    margin-bottom: 0;
  }
  &__title.icon-not-ready {
    background: url("/static/assets/not_ready_icon_ct.svg") no-repeat left center;
  }
  &__title.icon-fund-closed {
    background: url("/static/assets/fund_closed_icon_ct.svg") no-repeat left center;
  }
  &__title.icon-ready {
    background: url("/static/assets/check_icon_ct.svg") no-repeat left center;
  }
  &__title.icon-file-excel {
    background: url("/static/assets/file_excel_circle.svg") no-repeat left center;
  }
  &__title.icon-file-add {
    background: url("/static/assets/add_file_circle.svg") no-repeat left center;
  }
  &__content {
    border-bottom: 1px solid $mds-color-neutral-90;
    font-family: $mds-font-family-sans-serif;
    font-size: $mds-font-size-m;
    font-weight: $mds-font-weight-thin;
    padding-left: 48px; //Match design
    .table-heading {
      margin: $mds-space-inset-4-x 0;
    }
  }
  .import-lineup-error-download {
    float: right;
  }
}
.divider {
  border-top: 1px solid $mds-color-neutral-90;
  width: 95%;
}
.view-funds-btn {
  padding: $mds-space-inset-2-x 0;
}
.actions-bar {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding-bottom: $mds-space-2-x;
  margin-top: $mds-space-4-x;
  width: 100%;

  button {
    margin-left: $mds-space-inset-2-x;
  }
}
.guidelines-container {
  @include mds-body-text-s();
  padding: $mds-space-inset-3-x;
  border-color: $mds-feedback-color-success;
  border-style: solid;
  border-width: 1px;
}
.alternate-color-table {
  tr td:first-child,
  tr td:last-child {
    padding: $mds-space-inset-2-x;
  }
  tr:nth-child(odd) td {
    background-color: $mds-color-neutral-95;
  }
}
.warning-table {
  margin-bottom: $mds-space-inset-2-x;
  thead th {
    font-weight: $mds-font-weight-bold;
    padding: $mds-space-inset-2-x 0;
  }
  tbody td.warning-body-cell {
    border: 0;
    padding: $mds-space-inset-2-x 0;
    &:first-child {
      padding-left: $mds-space-inset-2-x;
    }
  }
}
.unknown-table {
  margin-bottom: $mds-space-inset-4-x;
}

.warnings-table-container {
  max-height: 400px;
  overflow: auto;
  @include horizontal-scroll-gradient;
}

.import-lineups-result__container {
  display: flex;
  min-height: 600px;
  flex-direction: column;
  justify-content: space-between;

  .actions-bar {
    align-self: flex-end;
  }
}
</style>
